import { useEffect, useRef } from "react";
import { Game } from "@/game/engine";
import { render } from "@/game/renderer";
import { loadSprites, Skin, Sprites } from "@/game/sprites";
import { loadSkin } from "@/game/storage";
import { Dir } from "@/game/types";

const KEYMAP: Record<string, Dir> = {
  ArrowUp: "up", KeyW: "up", ArrowDown: "down", KeyS: "down",
  ArrowLeft: "left", KeyA: "left", ArrowRight: "right", KeyD: "right",
};

interface Props { game: Game; width: number; height: number; onPauseKey: () => void; }

export const GameCanvas = ({ game, width, height, onPauseKey }: Props) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const spritesRef = useRef<Sprites>({});
  const keys = useRef<Dir[]>([]);

  useEffect(() => {
    spritesRef.current = loadSprites((s) => { spritesRef.current = s; }, loadSkin() as Skin);
  }, []);

  useEffect(() => {
    const canvas = ref.current!;
    const ctx = canvas.getContext("2d")!;
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = Math.max(0, Math.min(50, now - last));
      last = now;
      game.update(dt);
      const dpr = window.devicePixelRatio || 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      render(ctx, game.state, spritesRef.current, width, height, now);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [game, width, height]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const d = KEYMAP[e.code];
      if (d) {
        e.preventDefault();
        keys.current = [d, ...keys.current.filter((k) => k !== d)];
        game.setHeld(d);
      } else if (e.code === "Space" && game.state.phase === "playing") {
        e.preventDefault();
        game.pushAction();
      } else if (e.code === "Escape" || e.code === "KeyP") {
        if (e.code === "Escape" && (document.fullscreenElement || (document as Document & { webkitFullscreenElement?: Element }).webkitFullscreenElement)) return;
        onPauseKey();
      }
    };
    const up = (e: KeyboardEvent) => {
      const d = KEYMAP[e.code];
      if (!d) return;
      keys.current = keys.current.filter((k) => k !== d);
      game.setHeld(keys.current[0] ?? null);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [game, onPauseKey]);

  useEffect(() => {
    const el = ref.current!;
    let start: { x: number; y: number } | null = null;
    let moved = false;
    const ts = (e: TouchEvent) => { e.preventDefault(); const t = e.touches[0]; start = { x: t.clientX, y: t.clientY }; moved = false; };
    const tm = (e: TouchEvent) => {
      e.preventDefault();
      if (!start) return;
      const t = e.touches[0];
      const dx = t.clientX - start.x, dy = t.clientY - start.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 28) return;
      moved = true;
      const d: Dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : dy > 0 ? "down" : "up";
      game.setSwipe(d);
      start = { x: t.clientX, y: t.clientY };
    };
    const te = (e: TouchEvent) => { e.preventDefault(); if (start && !moved) game.pushAction(); start = null; };
    el.addEventListener("touchstart", ts, { passive: false });
    el.addEventListener("touchmove", tm, { passive: false });
    el.addEventListener("touchend", te, { passive: false });
    return () => { el.removeEventListener("touchstart", ts); el.removeEventListener("touchmove", tm); el.removeEventListener("touchend", te); };
  }, [game]);

  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  return (
    <canvas
      ref={ref}
      data-testid="arcade-game-canvas"
      width={width * dpr}
      height={height * dpr}
      style={{ width, height, touchAction: "none" }}
      className="rounded-2xl border-4 border-white/80 shadow-[0_12px_40px_rgba(0,0,0,0.45)] block"
    />
  );
};
